import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ArrowLeft, Send, Sparkles, Building2, Tag, Crown, UserCheck, Plane, Camera, Image, X, Upload } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NewIssue = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showCustomSource, setShowCustomSource] = useState(false);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    room_number: "",
    guest_name: "",
    guest_type: "REGULAR",
    guest_status: "IN_HOUSE",
    booking_source: "Walk-in",
    custom_source: "",
    check_in_date: "",
    check_out_date: "",
    incident_date: new Date().toISOString().slice(0, 16),
    description: "",
  });

  // Handle file upload
  const handleFileUpload = async (files) => {
    if (!files || files.length === 0) return;
    
    setUploading(true);
    const newImages = [];
    
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append("file", file);
        
        const res = await axios.post(`${API}/upload/image`, formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
        
        newImages.push(res.data.url);
      } catch (err) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    
    setUploadedImages([...uploadedImages, ...newImages]);
    setUploading(false);
  };

  // Handle camera capture
  const handleCameraCapture = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      
      const res = await axios.post(`${API}/upload/image`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      setUploadedImages([...uploadedImages, res.data.url]);
      toast.success("Photo captured!");
    } catch (err) {
      toast.error("Failed to capture photo");
    } finally {
      setUploading(false);
    }
  };

  // Remove uploaded image
  const removeImage = (index) => {
    setUploadedImages(uploadedImages.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.room_number || !formData.guest_name || !formData.description) {
      toast.error("Please fill Room Number, Guest Name, and Description");
      return;
    }

    setLoading(true);
    try {
      const bookingSource = formData.booking_source === "Other" 
        ? formData.custom_source 
        : formData.booking_source;
        
      const res = await axios.post(`${API}/issues`, {
        room_number: formData.room_number,
        guest_name: formData.guest_name,
        guest_type: formData.guest_type,
        guest_status: formData.guest_status,
        booking_source: bookingSource,
        check_in_date: formData.check_in_date ? new Date(formData.check_in_date).toISOString() : null,
        check_out_date: formData.check_out_date ? new Date(formData.check_out_date).toISOString() : null,
        incident_occurred_at: formData.incident_date ? new Date(formData.incident_date).toISOString() : null,
        description: formData.description,
        images: uploadedImages,
      });
      toast.success(
        <div>
          <p className="font-bold">Issue {res.data.issue_number} created!</p>
          <p className="text-sm mt-1">
            AI detected: {res.data.category} → {res.data.department_owner}
          </p>
        </div>
      );
      navigate(`/issues/${res.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create issue");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-2xl mx-auto space-y-6 animate-fade-in">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate("/issues")} data-testid="back-btn">
            <ArrowLeft size={18} />
          </Button>
          <div>
            <h1 className="font-heading text-3xl font-bold tracking-tight uppercase">
              New Issue
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Report a new guest issue or feedback
            </p>
          </div>
        </div>

        {/* AI Info Card */}
        <Card className="orbit-card border-primary/30 bg-primary/5">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-primary/20 rounded-sm flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="font-heading text-sm uppercase tracking-wider text-primary mb-1">
                  AI Auto-Classification
                </p>
                <p className="text-xs text-muted-foreground">
                  Cukup tulis deskripsi masalah/feedback, AI akan otomatis mendeteksi:
                </p>
                <div className="flex flex-wrap gap-2 mt-2">
                  <Badge variant="outline" className="text-xs">
                    <Tag size={10} className="mr-1" /> Category
                  </Badge>
                  <Badge variant="outline" className="text-xs">
                    <Building2 size={10} className="mr-1" /> Department
                  </Badge>
                  <Badge variant="outline" className="text-xs">
                    Severity (S1-S4)
                  </Badge>
                  <Badge variant="outline" className="text-xs">
                    Sentiment Score
                  </Badge>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Form */}
        <Card className="orbit-card">
          <CardHeader>
            <CardTitle className="font-heading text-sm uppercase tracking-wider">
              Issue Details
            </CardTitle>
            <CardDescription>
              Isi identitas tamu dan deskripsi lengkap. AI akan menganalisis sisanya.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="room" className="text-xs uppercase tracking-wider text-muted-foreground">
                    Room Number *
                  </Label>
                  <Input
                    id="room"
                    value={formData.room_number}
                    onChange={(e) => setFormData({ ...formData, room_number: e.target.value })}
                    placeholder="e.g., 301"
                    required
                    className="bg-background"
                    data-testid="room-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="guest" className="text-xs uppercase tracking-wider text-muted-foreground">
                    Guest Name *
                  </Label>
                  <Input
                    id="guest"
                    value={formData.guest_name}
                    onChange={(e) => setFormData({ ...formData, guest_name: e.target.value })}
                    placeholder="e.g., John Doe"
                    required
                    className="bg-background"
                    data-testid="guest-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="incident_date" className="text-xs uppercase tracking-wider text-muted-foreground">
                    Incident Date/Time
                  </Label>
                  <Input
                    id="incident_date"
                    type="datetime-local"
                    value={formData.incident_date}
                    onChange={(e) => setFormData({ ...formData, incident_date: e.target.value })}
                    className="bg-background"
                    data-testid="incident-date-input"
                  />
                </div>
              </div>

              {/* Guest Identity Section */}
              <div className="p-4 bg-surface-highlight/50 rounded-sm space-y-4">
                <p className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                  <UserCheck size={14} />
                  Guest Identity & Status
                </p>
                
                <div className="grid grid-cols-2 gap-4">
                  {/* Guest Type */}
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                      <Crown size={12} /> Guest Type
                    </Label>
                    <Select
                      value={formData.guest_type}
                      onValueChange={(value) => setFormData({ ...formData, guest_type: value })}
                    >
                      <SelectTrigger className="bg-background" data-testid="guest-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="REGULAR">
                          <span className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-zinc-500"></span>
                            Regular Guest
                          </span>
                        </SelectItem>
                        <SelectItem value="VIP">
                          <span className="flex items-center gap-2">
                            <Crown size={12} className="text-purple-500" />
                            VIP Guest
                          </span>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Guest Status */}
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                      Guest Status
                    </Label>
                    <Select
                      value={formData.guest_status}
                      onValueChange={(value) => setFormData({ ...formData, guest_status: value })}
                    >
                      <SelectTrigger className="bg-background" data-testid="guest-status-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ARRIVAL">
                          <span className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                            Arrival (Check-in)
                          </span>
                        </SelectItem>
                        <SelectItem value="IN_HOUSE">
                          <span className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                            In-House (Staying)
                          </span>
                        </SelectItem>
                        <SelectItem value="DEPARTURE">
                          <span className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                            Departure (Check-out)
                          </span>
                        </SelectItem>
                        <SelectItem value="OUTSIDER">
                          <span className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-gray-400"></span>
                            Outsider (Non-guest)
                          </span>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Check In / Check Out Dates */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                      Check In Date
                    </Label>
                    <Input
                      type="date"
                      value={formData.check_in_date}
                      onChange={(e) => setFormData({ ...formData, check_in_date: e.target.value })}
                      className="bg-background"
                      data-testid="check-in-date-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                      Check Out Date
                    </Label>
                    <Input
                      type="date"
                      value={formData.check_out_date}
                      onChange={(e) => setFormData({ ...formData, check_out_date: e.target.value })}
                      className="bg-background"
                      data-testid="check-out-date-input"
                    />
                  </div>
                </div>

                {/* Booking Source */}
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                    <Plane size={12} /> Booking Source
                  </Label>
                  <Select
                    value={formData.booking_source}
                    onValueChange={(value) => {
                      setFormData({ ...formData, booking_source: value });
                      setShowCustomSource(value === "Other");
                    }}
                  >
                    <SelectTrigger className="bg-background" data-testid="booking-source-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Walk-in">Walk-in</SelectItem>
                      <SelectItem value="Agoda">Agoda</SelectItem>
                      <SelectItem value="Booking.com">Booking.com</SelectItem>
                      <SelectItem value="Traveloka">Traveloka</SelectItem>
                      <SelectItem value="Tiket.com">Tiket.com</SelectItem>
                      <SelectItem value="Expedia">Expedia</SelectItem>
                      <SelectItem value="Direct Booking">Direct Booking</SelectItem>
                      <SelectItem value="Travel Agent">Travel Agent</SelectItem>
                      <SelectItem value="Corporate">Corporate</SelectItem>
                      <SelectItem value="Other">Other...</SelectItem>
                    </SelectContent>
                  </Select>
                  {showCustomSource && (
                    <Input
                      value={formData.custom_source}
                      onChange={(e) => setFormData({ ...formData, custom_source: e.target.value })}
                      placeholder="Specify booking source..."
                      className="bg-background mt-2"
                      data-testid="custom-source-input"
                    />
                  )}
                </div>
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label htmlFor="description" className="text-xs uppercase tracking-wider text-muted-foreground">
                  Description *
                </Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={5}
                  required
                  className="bg-background resize-none"
                  data-testid="description-input"
                />
              </div>

              {/* Photo Upload Section */}
              <div className="space-y-3">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                  <Camera size={14} />
                  Foto/Gambar (Opsional)
                </Label>
                
                <div className="flex gap-2">
                  {/* Take Photo Button */}
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => cameraInputRef.current?.click()}
                    disabled={uploading}
                    className="flex-1"
                    data-testid="take-photo-btn"
                  >
                    <Camera size={16} className="mr-2" />
                    Ambil Foto
                  </Button>
                  <input
                    ref={cameraInputRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={handleCameraCapture}
                    className="hidden"
                  />
                  
                  {/* Upload File Button */}
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="flex-1"
                    data-testid="upload-file-btn"
                  >
                    <Upload size={16} className="mr-2" />
                    Upload File
                  </Button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(e) => handleFileUpload(e.target.files)}
                    className="hidden"
                  />
                </div>
                
                {uploading && (
                  <p className="text-xs text-muted-foreground">Uploading...</p>
                )}

                {/* Preview uploaded images */}
                {uploadedImages.length > 0 && (
                  <div className="grid grid-cols-4 gap-2 mt-3">
                    {uploadedImages.map((url, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={`${process.env.REACT_APP_BACKEND_URL}${url}`}
                          alt={`Upload ${index + 1}`}
                          className="w-full h-20 object-cover rounded border border-border"
                        />
                        <button
                          type="button"
                          onClick={() => removeImage(index)}
                          className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X size={12} className="text-white" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Submit Button */}
              <div className="flex justify-end pt-4 border-t border-border">
                <Button type="submit" disabled={loading} className="min-w-[140px]" data-testid="submit-btn">
                  {loading ? (
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Analyzing...
                    </div>
                  ) : (
                    <>
                      <Send size={16} className="mr-2" />
                      Create Issue
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default NewIssue;
